import dateFormat from "dateformat";

// monitoring_deadline / monitoring_new_deadline are always computed on the
// backend as 19:59:59.999 UTC, i.e. 23:59:59 Georgia time (see
// monitoring-backend/utility/dates/index.js addOneMonthDeadline). Formatting
// with the viewer's local timezone only shows 23:59:59 correctly for viewers
// in Georgia, so the date part is read in UTC (safe — no midnight-crossing
// risk since the UTC hour is always the afternoon) and the time is hardcoded.
const formatMonitoringDeadline = (date: Date | string | null | undefined): string =>
  date ? `${dateFormat(date, "dd/mm/yyyy", true)} 23:59:59` : "";

// Mirrors monitoring-backend/utility/dates/index.js snapToGeorgiaEndOfDay.
// A deadline always means "end of that day, Georgia time" regardless of what
// time-of-day is actually stored — snapping at comparison time means old
// records saved with the wrong time-of-day self-correct without a migration.
const snapToGeorgiaEndOfDay = (date: Date | string): Date => {
  const d = new Date(date);
  d.setUTCHours(19, 59, 59, 999);
  return d;
};

const convertToMonthsAndDays = (ms: number): { months: number; days: number } => {
  const MS_IN_A_DAY = 1000 * 60 * 60 * 24;
  const AVERAGE_DAYS_IN_A_MONTH = 30.44; // Approximate days in a month

  const totalDays = (ms ?? 0) / MS_IN_A_DAY;
  const months = Math.floor(totalDays / AVERAGE_DAYS_IN_A_MONTH);
  const days = Math.round(totalDays % AVERAGE_DAYS_IN_A_MONTH);

  return { months, days };
};

const convertToMilliseconds = (months: number, days: number): number => {
  const MS_IN_A_DAY = 1000 * 60 * 60 * 24;
  const AVERAGE_DAYS_IN_A_MONTH = 30.44;

  const daysFromMonths = months * AVERAGE_DAYS_IN_A_MONTH;
  const totalDays = daysFromMonths + days;

  return totalDays * MS_IN_A_DAY;
};

const generateNewDeadline = (dayMonth: number, oldDate: Date | null): Date | null => {
  if (dayMonth === 0) {
    if (oldDate) {
      return new Date(oldDate);
    } else {
      return null;
    }
  }
  const deadlineDate = new Date().getTime() + dayMonth;
  // Match backend addOneMonthDeadline: always store 23:59:59 Georgia time
  // (19:59:59.999 UTC), regardless of what moment the form was filled in.
  return snapToGeorgiaEndOfDay(deadlineDate);
};

const isMoreThanMonth = (deadline: number, approvedDate: number): boolean => {
  return approvedDate > deadline;
};

const isExpire = (dateToCheck: Date | string, compareTo: Date | string): boolean => {
  if (!dateToCheck || !compareTo) return false;
  const a = new Date(dateToCheck);
  const b = new Date(compareTo);
  return a > b;
};

// Overdue check for a monitoring deadline specifically — always snaps the
// deadline to end-of-Georgia-day before comparing, so a deadline saved with
// the wrong time-of-day (old bug) doesn't wrongly read as already expired.
const isOverdue = (deadline: Date | string | null | undefined, comparisonInstant: Date | string = new Date()): boolean => {
  if (!deadline) return false;
  return new Date(comparisonInstant).getTime() > snapToGeorgiaEndOfDay(deadline).getTime();
};

const isMoreThanOneYear = (deadline: number, currentTime: number): boolean => {
  const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
  const newDeadline = currentTime + new Date().getTime();
  return newDeadline - deadline > oneYearInMs;
};
const isDeadline = (newDeadline: Date | null, deadline: Date | null): boolean => {
  const activeDeadline = newDeadline || deadline;
  if (!activeDeadline) return false;
  return isOverdue(activeDeadline);
};
export {
  isExpire,
  isDeadline,
  isMoreThanMonth,
  isOverdue,
  snapToGeorgiaEndOfDay,
  convertToMilliseconds,
  convertToMonthsAndDays,
  isMoreThanOneYear,
  generateNewDeadline,
  formatMonitoringDeadline,
};
